import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePmsTheme } from "../Components/PmsThemeContext";
import PmsConfirmDialog from "../Components/PmsConfirmDialog";
import {
  executeRmsRecommendationWorkflowAction,
  fetchRmsConfigMeta,
  fetchRmsOutputs,
  fetchRmsRecommendationWorkflow,
  previewRmsRecommendationWorkflowAction,
} from "./rmsApi";
import {
  formatCurrency,
  formatDateLabel,
  formatDateRange,
  formatPercent,
  safeArray,
  statusTone,
} from "./rmsShared";
import {
  createRmsTranslator,
  getRmsLocale,
  translateRmsStatus,
} from "./rmsPmsI18n";
import {
  normalizeFiniteAmount,
  normalizeCurrencyCode,
  normalizeSnapshotBoolean,
  resolveCurrencyPriceSummary,
} from "./rmsCurrencySummary";
import { resolveRecommendationProvenance } from "./rmsRecommendationProvenance";
import { resolveOrphanGapRecommendationEvidence } from "./rmsOrphanGapRecommendation";
import RmsPipelineStatus from "./RmsPipelineStatus.jsx";
import { resolveRmsPipelineStatus } from "./rmsPipelineStatus";
import {
  buildRmsWorkflowExecutePayload,
  buildRmsWorkflowPreviewPayload,
  canExecuteRmsWorkflowPreview,
  canRunRmsWorkflowAction,
  createRmsWorkflowIdempotencyKey,
  getRmsWorkflowActionCopy,
  getRmsWorkflowError,
  getRmsWorkflowStatusTone,
  normalizeRmsRecommendationWorkflow,
  normalizeRmsWorkflowExecutionResult,
  normalizeRmsWorkflowPreview,
  resolveRmsWorkflowRowStatus,
} from "./rmsRecommendationWorkflow";

const DAILY_LIMIT = 400;
const LONG_RANGE_LIMIT = 250;

function RunPill({ label, run, languageId }) {
  const status = run?.status || "missing";
  return (
    <span className={`rmo-pill rmo-pill--${statusTone(status)}`}>
      {label}: {translateRmsStatus(languageId, status)}
    </span>
  );
}

function RecommendationDelta({ recommendedPrice, currentPrice, locale, currency }) {
  const next = normalizeFiniteAmount(recommendedPrice);
  const current = normalizeFiniteAmount(currentPrice);
  const normalizedCurrency = normalizeCurrencyCode(currency);
  if (
    next == null ||
    current == null ||
    current === 0 ||
    !normalizedCurrency
  ) {
    return <span className="rmo-muted">-</span>;
  }

  const delta = next - current;
  const deltaPct = delta / current;
  const toneClass = delta >= 0 ? "rmo-deltaUp" : "rmo-deltaDown";

  return (
    <div className="rmo-cellStack">
      <div className={`rmo-cellMain ${toneClass}`}>
        {formatCurrency(delta, locale, normalizedCurrency)}
      </div>
      <div className={`rmo-cellMeta ${toneClass}`}>{formatPercent(deltaPct, 1, locale)}</div>
    </div>
  );
}

function SnapshotCurrencyValue({ value, locale, currency }) {
  const normalizedCurrency = normalizeCurrencyCode(currency);
  const amount = normalizeFiniteAmount(value);
  if (!normalizedCurrency || amount == null) {
    return <span className="rmo-muted">—</span>;
  }
  return formatCurrency(amount, locale, normalizedCurrency);
}

function CurrencyAverageValue({ priceSummary, locale }) {
  if (!priceSummary.entries.length) return <span className="rmo-muted">—</span>;

  if (priceSummary.status === "single") {
    const entry = priceSummary.entries[0];
    return formatCurrency(entry.avg_recommended_price, locale, entry.currency);
  }

  return (
    <div className="rmo-kpiCurrencyBreakdown">
      {priceSummary.entries.map((entry) => (
        <div key={entry.currency} className="rmo-kpiCurrencyLine">
          <span>{formatCurrency(entry.avg_recommended_price, locale, entry.currency)}</span>
          <span className="rmo-kpiCurrencyCode">{entry.currency}</span>
        </div>
      ))}
    </div>
  );
}

function RateProductTrace({ row, t }) {
  const roomRateName = String(row?.room_rate_name || "").trim();
  const roomRateCode = String(row?.room_rate_code || "").trim();
  const policyVersion = row?.rate_policy_version;
  if (!roomRateName && !roomRateCode && policyVersion == null) {
    return <span className="rmo-muted">-</span>;
  }

  return (
    <div className="rmo-cellStack" style={{ minWidth: 150 }}>
      <div className="rmo-cellMain">{roomRateName || roomRateCode || "-"}</div>
      <div className="rmo-cellMeta">
        {roomRateCode || "-"} · {t("Policy v{version}", { version: policyVersion ?? "-" })}
      </div>
    </div>
  );
}

function RecommendationOriginCell({ row, t }) {
  const provenance = resolveRecommendationProvenance(row);
  const modelTrace = [provenance.provider, provenance.model]
    .filter(Boolean)
    .join(" / ");
  const requestHash = provenance.requestHash
    ? `${provenance.requestHash.slice(0, 12)}…`
    : null;
  const responseHash = provenance.responseHash
    ? `${provenance.responseHash.slice(0, 12)}…`
    : null;

  return (
    <div className="rmo-aiOriginCell">
      <span className={`rmo-pill rmo-pill--${provenance.presentation.tone}`}>
        {t(provenance.presentation.labelKey)}
      </span>
      <div className="rmo-cellMeta">
        {t(provenance.presentation.descriptionKey)}
      </div>
      {provenance.rejected ? (
        <div className="rmo-aiRejectionNotice">
          <strong>
            {provenance.validationStatus === "rejected"
              ? t("AI rejected; deterministic result used.")
              : t("AI result not used; deterministic result used.")}
          </strong>
          {provenance.validationStatusLabelKey ? (
            <span>
              {t("AI validation: {status}", {
                status: t(provenance.validationStatusLabelKey),
              })}
            </span>
          ) : null}
          <span>
            {provenance.rejectionCodes.length
              ? t("Rejection codes: {codes}", {
                  codes: provenance.rejectionCodes.join(", "),
                })
              : t("Rejection details are unavailable.")}
          </span>
        </div>
      ) : null}
      {!provenance.rejected &&
      provenance.verified &&
      provenance.source !== "deterministic" &&
      provenance.validationStatusLabelKey ? (
        <div className="rmo-cellMeta">
          {t("AI validation: {status}", {
            status: t(provenance.validationStatusLabelKey),
          })}
        </div>
      ) : null}
      {provenance.anomalyCodes.length ? (
        <div className="rmo-cellMeta">
          {t("AI anomaly codes: {codes}", {
            codes: provenance.anomalyCodes.join(", "),
          })}
        </div>
      ) : null}
      {modelTrace ? (
        <div className="rmo-aiAuditTrace">
          {t("AI trace: {model} · {prompt}", {
            model: modelTrace,
            prompt: provenance.promptVersion || "—",
          })}
        </div>
      ) : null}
      {requestHash || responseHash ? (
        <div className="rmo-aiAuditTrace">
          {t("AI hashes: request {request} · response {response}", {
            request: requestHash || "—",
            response: responseHash || "—",
          })}
        </div>
      ) : null}
      {provenance.source && !provenance.verified && !provenance.rejected ? (
        <div className="rmo-cellMeta">{t("Source metadata could not be verified.")}</div>
      ) : null}
    </div>
  );
}

function readObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function resolvePriceResponseEvidence(row) {
  const signal = readObject(row?.signal_snapshot);
  const candidates = [
    row?.price_response,
    row?.price_response_snapshot,
    row?.response_snapshot,
    row?.control_snapshot,
    row?.lineage_snapshot,
    row?.decision_snapshot,
    readObject(row?.stats).price_response,
    signal.price_response,
    signal.price_response_snapshot,
  ];
  const source = candidates.map(readObject).find((item) => Object.keys(item).length) || {};
  const decision = readObject(
    source.row_decision || source.decision || source.controls || source.control
  );
  const state = readObject(source.state || source.history || decision);
  const signalEvidence = readObject(
    source.signal || source.pace || decision.confidence_evidence
  );
  const appliedConstraints = Array.isArray(decision.applied_constraints)
    ? decision.applied_constraints
    : Array.isArray(decision.binding_constraints)
      ? decision.binding_constraints
      : Array.isArray(source.applied_constraints)
        ? source.applied_constraints
        : [];
  return {
    currentPrice: decision.current_price ?? source.current_price ?? null,
    neutralPrice:
      source.neutral_price ?? decision.neutral_target_price ??
      decision.neutral_price ?? source.target_neutral_price ?? null,
    controlledPrice: decision.controlled_price ?? source.controlled_price ?? null,
    hysteresisState:
      decision.pace_state_after ?? signalEvidence.state_after ??
      source.hysteresis_state ?? state.pace_state ?? null,
    priorHysteresisState: decision.pace_state_before ?? signalEvidence.state_before ?? null,
    caps: appliedConstraints.map((constraint) =>
      typeof constraint === "string" ? constraint : constraint?.code || constraint?.type || constraint?.name
    ).filter(Boolean),
    runLower: decision.per_run_lower_price ?? decision.per_run_lower ?? source.per_run_lower ?? null,
    runUpper: decision.per_run_upper_price ?? decision.per_run_upper ?? source.per_run_upper ?? null,
    dayLower: decision.rolling_24h_lower_price ?? decision.rolling_24h_lower ?? decision.day_24h_lower ?? source.rolling_24h_lower ?? null,
    dayUpper: decision.rolling_24h_upper_price ?? decision.rolling_24h_upper ?? decision.day_24h_upper ?? source.rolling_24h_upper ?? null,
    historyCoverage:
      decision.history_coverage_complete ?? state.history_coverage ?? source.history_coverage ?? null,
    historyEvents: Array.isArray(decision.history_events)
      ? decision.history_events.length
      : state.events_count ?? state.history_events_count ?? null,
    effectiveConfidence:
      decision.confidence_evidence?.effective_confidence ?? row?.confidence ?? null,
    featurePresenceConfidence:
      decision.confidence_evidence?.feature_presence_score ?? null,
    forecastConfidence:
      decision.confidence_evidence?.forecast_confidence ??
      signalEvidence.forecast_confidence ?? source.forecast_confidence ?? signal.forecast_confidence ?? null,
    confidenceThreshold:
      decision.confidence_evidence?.low_confidence_threshold ??
      decision.low_confidence_threshold ?? source.low_confidence_threshold ?? null,
    confidenceAction: decision.low_confidence_action ?? source.low_confidence_action ?? null,
    isLowConfidence:
      decision.confidence_evidence?.is_low_confidence === true,
    approvalRequired:
      decision.approval_required === true || source.approval_required === true ||
      row?.apply_status === "approval_required",
    approvalReasons: Array.isArray(decision.approval_reasons)
      ? decision.approval_reasons
      : Array.isArray(source.approval_reasons)
        ? source.approval_reasons
        : [],
  };
}

function PriceResponseCell({ row, locale, currency, t }) {
  const evidence = resolvePriceResponseEvidence(row);
  const hasEvidence = evidence.neutralPrice != null || evidence.hysteresisState || evidence.caps.length;
  if (!hasEvidence) return <span className="rmo-muted">—</span>;
  const hasRunRange = evidence.runLower != null || evidence.runUpper != null;
  const hasDayRange = evidence.dayLower != null || evidence.dayUpper != null;
  return (
    <div className="rmo-cellStack" style={{ minWidth: 205 }}>
      <div className="rmo-cellMain">
        {t("Neutral")}: <SnapshotCurrencyValue value={evidence.neutralPrice} locale={locale} currency={currency} />
      </div>
      {evidence.currentPrice != null || evidence.controlledPrice != null ? (
        <div className="rmo-cellMeta">
          {t("Applied → controlled")}: <SnapshotCurrencyValue value={evidence.currentPrice} locale={locale} currency={currency} /> → <SnapshotCurrencyValue value={evidence.controlledPrice} locale={locale} currency={currency} />
        </div>
      ) : null}
      {evidence.hysteresisState ? (
        <div className="rmo-cellMeta">
          {t("Pace state")}: {evidence.priorHysteresisState ? `${evidence.priorHysteresisState} → ` : ""}{evidence.hysteresisState}
        </div>
      ) : null}
      {evidence.caps.length ? (
        <div className="rmo-cellMeta">{t("Binding")}: {evidence.caps.join(", ")}</div>
      ) : null}
      {hasRunRange ? (
        <div className="rmo-cellMeta">
          {t("Run cap")}: <SnapshotCurrencyValue value={evidence.runLower} locale={locale} currency={currency} /> – <SnapshotCurrencyValue value={evidence.runUpper} locale={locale} currency={currency} />
        </div>
      ) : null}
      {hasDayRange ? (
        <div className="rmo-cellMeta">
          {t("24h cap")}: <SnapshotCurrencyValue value={evidence.dayLower} locale={locale} currency={currency} /> – <SnapshotCurrencyValue value={evidence.dayUpper} locale={locale} currency={currency} />
        </div>
      ) : null}
      {evidence.historyCoverage != null ? (
        <div className="rmo-cellMeta">
          {t("History")}: {evidence.historyCoverage ? t("Complete") : t("Incomplete")}{evidence.historyEvents != null ? ` · ${evidence.historyEvents}` : ""}
        </div>
      ) : null}
    </div>
  );
}

function ConfidenceControlCell({ row, locale, t }) {
  const evidence = resolvePriceResponseEvidence(row);
  const hasEvidence = evidence.effectiveConfidence != null || evidence.confidenceAction || evidence.approvalRequired;
  if (!hasEvidence) return <span className="rmo-muted">—</span>;
  return (
    <div className="rmo-cellStack" style={{ minWidth: 190 }}>
      {evidence.effectiveConfidence != null ? (
        <div className="rmo-cellMain">
          {t("Effective evidence")}: {formatPercent(evidence.effectiveConfidence, 1, locale)}
          {evidence.confidenceThreshold != null ? ` / ${formatPercent(evidence.confidenceThreshold, 1, locale)}` : ""}
        </div>
      ) : null}
      {evidence.forecastConfidence != null || evidence.featurePresenceConfidence != null ? (
        <div className="rmo-cellMeta">
          {t("Forecast / feature")}: {evidence.forecastConfidence != null ? formatPercent(evidence.forecastConfidence, 1, locale) : "—"} / {evidence.featurePresenceConfidence != null ? formatPercent(evidence.featurePresenceConfidence, 1, locale) : "—"}
        </div>
      ) : null}
      {evidence.confidenceAction && (evidence.isLowConfidence || evidence.confidenceAction !== "none") ? (
        <div className="rmo-cellMeta">{t("Low confidence")}: {String(evidence.confidenceAction)}</div>
      ) : null}
      {evidence.approvalRequired ? (
        <div className="rmo-aiRejectionNotice">
          <strong>{t("Approval required")}</strong>
          {evidence.approvalReasons.length ? <span>{evidence.approvalReasons.join(", ")}</span> : null}
        </div>
      ) : null}
    </div>
  );
}

function OrphanGapActionCell({ row, locale, currency, t }) {
  const evidence = resolveOrphanGapRecommendationEvidence(row);
  if (!evidence) return <span className="rmo-muted">—</span>;
  const readableCode = (value) =>
    String(value || "")
      .replaceAll("_", " ")
      .replaceAll("-", " ");
  const horizonBandLabel =
    evidence.horizonBand === "near_0_7"
      ? t("0–7 days")
      : evidence.horizonBand === "middle_8_21"
        ? t("8–21 days")
        : evidence.horizonBand === "far_22_plus"
          ? t("22+ days")
          : readableCode(evidence.horizonBand) || "—";
  const t06Label =
    (evidence.t06Status ? t(readableCode(evidence.t06Status)) : null) ||
    (evidence.t06Eligible === true
      ? t("Certified")
      : evidence.t06Eligible === false
        ? t("Not certified")
        : t("No certified gap"));
  return (
    <div className="rmo-cellStack" style={{ minWidth: 260 }}>
      <div className="rmo-cellMain">
        {t("Horizon")}: {horizonBandLabel}
        {evidence.leadDays != null ? ` · ${evidence.leadDays} ${t("days")}` : ""}
      </div>
      <div className="rmo-cellMeta">
        {t("Certified gap")}: {evidence.gapNights != null ? `${evidence.gapNights} ${t("nights")}` : "—"} · {t06Label}
      </div>
      {evidence.paceStatus || evidence.weakDemand != null ? (
        <div className="rmo-cellMeta">
          {t("Demand evidence")}: {evidence.paceStatus
            ? t(readableCode(evidence.paceStatus))
            : "—"}
          {evidence.weakDemand != null
            ? ` · ${evidence.weakDemand ? t("weak certified") : t("not weak")}`
            : ""}
          {evidence.paceExcessPct != null
            ? ` · ${formatPercent(evidence.paceExcessPct, 1, locale)}`
            : ""}
        </div>
      ) : null}
      {evidence.effectiveConfidence != null || evidence.lowConfidence != null ? (
        <div className="rmo-cellMeta">
          {t("Confidence")}: {evidence.effectiveConfidence != null
            ? formatPercent(evidence.effectiveConfidence, 1, locale)
            : "—"}
          {evidence.lowConfidenceThreshold != null
            ? ` / ${formatPercent(evidence.lowConfidenceThreshold, 1, locale)}`
            : ""}
          {evidence.lowConfidence === true ? ` · ${t("Low")}` : ""}
        </div>
      ) : null}
      {evidence.minLosBefore != null || evidence.minLosAfter != null ? (
        <div className="rmo-cellMeta">
          {t("MinLOS before → after")}: {evidence.minLosBefore ?? "—"} → {evidence.minLosAfter ?? "—"}
        </div>
      ) : null}
      {evidence.preOrphanPrice != null || evidence.postOrphanPrice != null ? (
        <div className="rmo-cellMeta">
          {t("Price before → after")}: <SnapshotCurrencyValue value={evidence.preOrphanPrice} locale={locale} currency={evidence.currency || currency} /> → <SnapshotCurrencyValue value={evidence.postOrphanPrice} locale={locale} currency={evidence.currency || currency} />
        </div>
      ) : null}
      {evidence.strongDecision ? (
        <>
          <div className="rmo-cellMeta">
            {t("Current / protection anchor / proposal")}: {" "}
            <SnapshotCurrencyValue
              value={evidence.currentAppliedPrice}
              locale={locale}
              currency={evidence.currency || currency}
            />{" / "}
            <SnapshotCurrencyValue
              value={evidence.protectionAnchorPrice}
              locale={locale}
              currency={evidence.currency || currency}
            />{" / "}
            <SnapshotCurrencyValue
              value={evidence.priceAfterStrong}
              locale={locale}
              currency={evidence.currency || currency}
            />
          </div>
          <div className="rmo-cellMeta">
            {t("Requested → effective premium")}: {evidence.requestedPremiumPct != null
              ? formatPercent(evidence.requestedPremiumPct, 1, locale)
              : "—"}{" → "}
            {evidence.effectivePremiumPct != null
              ? formatPercent(evidence.effectivePremiumPct, 1, locale)
              : "—"}
          </div>
          <div className="rmo-cellMeta">
            {t("Premium caps (configured / pace / ceiling / movement)")}: {[
              evidence.configuredPremiumCapPct,
              evidence.pacePremiumCapPct,
              evidence.ceilingPremiumCapPct,
              evidence.movementPremiumCapPct,
            ]
              .map((value) =>
                value == null ? "—" : formatPercent(value, 1, locale)
              )
              .join(" / ")}
          </div>
          <div className="rmo-cellMeta">
            {t("Last-room clearance")}: {evidence.inventoryReady === true
              ? t("Ready")
              : evidence.inventoryReady === false
                ? t("Not ready")
                : "—"}
            {evidence.lastRoomAvailable != null
              ? ` · ${evidence.lastRoomAvailable ? t("last room certified") : t("last room not certified")}`
              : ""}
            {evidence.noLongerStayDisplacement != null
              ? ` · ${evidence.noLongerStayDisplacement ? t("no longer-stay displacement") : t("longer-stay displacement possible")}`
              : ""}
          </div>
          <div className="rmo-cellMeta">
            {t("Safety")}: {t("discount")} {evidence.strongEffectiveDiscountPct != null
              ? formatPercent(evidence.strongEffectiveDiscountPct, 1, locale)
              : "—"}{" · "}
            {evidence.restrictionsUnchangedFromT08 === true
              ? t("restrictions unchanged")
              : t("restriction state unavailable")}
          </div>
        </>
      ) : null}
      {evidence.requestedDiscountPct != null || evidence.effectiveDiscountPct != null ? (
        <div className="rmo-cellMeta">
          {t("Requested → effective discount")}: {evidence.requestedDiscountPct != null ? formatPercent(evidence.requestedDiscountPct, 1, locale) : "—"} → {evidence.effectiveDiscountPct != null ? formatPercent(evidence.effectiveDiscountPct, 1, locale) : "—"}
        </div>
      ) : null}
      {evidence.effectiveFloorPrice != null || evidence.variableCost != null ? (
        <div className="rmo-cellMeta">
          {t("Economic floor / variable cost")}: <SnapshotCurrencyValue value={evidence.effectiveFloorPrice} locale={locale} currency={evidence.currency || currency} /> / <SnapshotCurrencyValue value={evidence.variableCost} locale={locale} currency={evidence.currency || currency} />
          {evidence.distributionCostPct != null
            ? ` + ${formatPercent(evidence.distributionCostPct, 1, locale)}`
            : ""}
        </div>
      ) : null}
      {evidence.bindingConstraints.length ? (
        <div className="rmo-cellMeta">
          {t("Binding")}: {evidence.bindingConstraints.join(", ")}
        </div>
      ) : null}
      <div className="rmo-cellMeta">
        {t("Decision")}: {evidence.action
          ? t(readableCode(evidence.action))
          : "—"}
        {evidence.reasonCode ? ` · ${evidence.reasonCode}` : ""}
      </div>
      {evidence.approvalRequired ? (
        <div className="rmo-aiRejectionNotice">
          <strong>{t("Approval required")}</strong>
          {evidence.approvalReasons.length ? (
            <span>{evidence.approvalReasons.join(", ")}</span>
          ) : null}
        </div>
      ) : null}
      {evidence.activationApplied ||
      evidence.priceApplied ||
      evidence.restrictionsApplied ||
      evidence.automaticApplicationAllowed === true ||
      (evidence.strongDecision && evidence.strongEffectiveDiscountPct !== 0) ||
      (evidence.strongDecision && evidence.restrictionsUnchangedFromT08 !== true) ||
      (evidence.strongDecision && evidence.ctaCtdUnchanged !== true) ||
      (evidence.strongDecision && evidence.stopSellUnchanged !== true) ? (
        <div className="rmo-errorBox">{t("Unexpected automatic activation")}</div>
      ) : (
        <div className="rmo-cellMeta">{t("Proposal only; not applied")}</div>
      )}
    </div>
  );
}

const WORKFLOW_FIELD_LABELS = Object.freeze({
  total_rows: "Total rows",
  actionable_rows: "Actionable rows",
  executable_rows: "Executable rows",
  pending_rows: "Pending rows",
  approval_required_rows: "Approval-required rows",
  held_by_policy_rows: "Held-by-policy rows",
  no_op_rows: "Certified no-op rows",
  changed_rows: "Changed rows",
  applied_rows: "Applied rows",
  rollback_rows: "Rollback rows",
  price_rows: "Price rows",
  los_rows: "Length-of-stay rows",
});

function humanizeWorkflowField(field) {
  if (WORKFLOW_FIELD_LABELS[field]) return WORKFLOW_FIELD_LABELS[field];
  return String(field || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function workflowSummaryEntries(summary) {
  return Object.entries(readObject(summary))
    .filter(([, value]) =>
      value == null || ["string", "number", "boolean"].includes(typeof value)
    )
    .slice(0, 12);
}

function WorkflowSummaryGrid({ summary, locale, t }) {
  const entries = workflowSummaryEntries(summary);
  if (!entries.length) return null;
  return (
    <div className="rmo-workflowSummaryGrid">
      {entries.map(([field, value]) => (
        <div className="rmo-workflowMetric" key={field}>
          <span>{t(humanizeWorkflowField(field))}</span>
          <strong>
            {typeof value === "number" ? value.toLocaleString(locale) : String(value ?? "—")}
          </strong>
        </div>
      ))}
    </div>
  );
}

function WorkflowBlockers({ blockers, t }) {
  if (!safeArray(blockers).length) return null;
  return (
    <div className="rmo-workflowBlockers">
      <strong>{t("This operation is safely blocked")}</strong>
      <ul>
        {blockers.map((blocker, index) => (
          <li key={`${blocker?.code || "blocked"}-${index}`}>
            {blocker?.message || blocker?.code || t("Unknown blocker")}
          </li>
        ))}
      </ul>
    </div>
  );
}

function WorkflowPreviewBody({ preview, note, onNoteChange, locale, t }) {
  const visibleRows = safeArray(preview?.rows).slice(0, 8);
  return (
    <div className="rmo-workflowPreview">
      <p className="pcd-message">
        {t(
          "This preview covers the complete recommendation run. Partial selection is not permitted."
        )}
      </p>
      <WorkflowSummaryGrid summary={preview?.summary} locale={locale} t={t} />
      <WorkflowBlockers blockers={preview?.blockers} t={t} />
      {visibleRows.length ? (
        <div className="rmo-workflowPreviewRows">
          {visibleRows.map((row, index) => (
            <div key={row?.recommendation_id || index}>
              <strong>
                {row?.room_category_name || row?.room_rate_name || t("Recommendation")}
              </strong>
              <span>{row?.stay_date || row?.date || `#${row?.recommendation_id || index + 1}`}</span>
              {row?.before?.price != null || row?.proposed?.price != null ? (
                <span>
                  {row?.before?.price ?? "—"} → {row?.proposed?.price ?? "—"}
                </span>
              ) : null}
            </div>
          ))}
          {safeArray(preview?.rows).length > visibleRows.length ? (
            <small>
              {t("And {count} more rows", {
                count: safeArray(preview.rows).length - visibleRows.length,
              })}
            </small>
          ) : null}
        </div>
      ) : null}
      <label className="rmo-workflowNote">
        <span>{t("Audit note (optional)")}</span>
        <textarea
          className="rmo-textarea"
          value={note}
          maxLength={500}
          onChange={(event) => onNoteChange(event.target.value)}
          placeholder={t("Add context for the audit trail")}
        />
      </label>
      <div className="rmo-cellMeta">
        {t("Preview expires at {time}", {
          time: preview?.expiresAt
            ? new Date(preview.expiresAt).toLocaleString(locale)
            : "—",
        })}
      </div>
    </div>
  );
}

export default function RmsRecommendationsView({
  businessUnitId,
  canApproveRecommendations = false,
  canApplyRecommendations = false,
  canRollbackRecommendations = false,
}) {
  const { languageId, language } = usePmsTheme();
  const t = useMemo(() => createRmsTranslator(languageId), [languageId]);
  const locale = getRmsLocale(languageId, language);
  const dateLocale = languageId === "en" ? "en-GB" : locale || "es-ES";
  const [meta, setMeta] = useState(null);
  const [summary, setSummary] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [longRange, setLongRange] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [pinnedPipelineId, setPinnedPipelineId] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [dailyLoading, setDailyLoading] = useState(true);
  const [longRangeLoading, setLongRangeLoading] = useState(true);
  const [workflowLoading, setWorkflowLoading] = useState(true);
  const [error, setError] = useState("");
  const [workflowError, setWorkflowError] = useState("");
  const [workflowNotice, setWorkflowNotice] = useState("");
  const [workflowActionBusy, setWorkflowActionBusy] = useState("");
  const [workflowDialog, setWorkflowDialog] = useState(null);
  const [workflowNote, setWorkflowNote] = useState("");
  const [workflowPreviewClock, setWorkflowPreviewClock] = useState(() => Date.now());
  const [roomCategoryId, setRoomCategoryId] = useState("");
  const [dailyDateFrom, setDailyDateFrom] = useState("");
  const [dailyDateTo, setDailyDateTo] = useState("");
  const [longDateFrom, setLongDateFrom] = useState("");
  const [longDateTo, setLongDateTo] = useState("");
  const snapshotRequestRef = useRef(0);

  useEffect(() => {
    const expiresAt = workflowDialog?.preview?.expiresAt;
    if (!expiresAt) return undefined;
    setWorkflowPreviewClock(Date.now());
    const delay = Math.max(0, new Date(expiresAt).getTime() - Date.now()) + 25;
    const timeoutId = window.setTimeout(
      () => setWorkflowPreviewClock(Date.now()),
      Math.min(delay, 2147483647)
    );
    return () => window.clearTimeout(timeoutId);
  }, [workflowDialog]);

  useEffect(() => {
    let cancelled = false;

    const loadMeta = async () => {
      try {
        const payload = await fetchRmsConfigMeta();
        if (cancelled) return;
        setMeta(payload);
        setDailyDateFrom(
          (current) => current || payload?.defaults?.recommendations_date_from || ""
        );
        setDailyDateTo(
          (current) => current || payload?.defaults?.recommendations_date_to || ""
        );
        setLongDateFrom(
          (current) => current || payload?.defaults?.long_range_date_from || ""
        );
        setLongDateTo(
          (current) => current || payload?.defaults?.long_range_date_to || ""
        );
      } catch (nextError) {
        if (!cancelled) {
          setError(
              nextError?.response?.data?.error ||
              nextError?.message ||
              t("Could not load RMS meta.")
          );
        }
      }
    };

    loadMeta();

    return () => {
      cancelled = true;
    };
  }, [t]);

  const loadSnapshot = useCallback(async () => {
    const requestId = snapshotRequestRef.current + 1;
    snapshotRequestRef.current = requestId;
    const hasCompleteFilters =
      dailyDateFrom && dailyDateTo && longDateFrom && longDateTo;

    setError("");
    setSummary(null);
    setRecommendations(null);
    setLongRange(null);
    setWorkflow(null);
    setPinnedPipelineId(null);
    setWorkflowError("");
    setWorkflowNotice("");

    if (!hasCompleteFilters) {
      setSummaryLoading(false);
      setDailyLoading(false);
      setLongRangeLoading(false);
      setWorkflowLoading(false);
      return;
    }

    setSummaryLoading(true);
    setDailyLoading(true);
    setLongRangeLoading(true);
    setWorkflowLoading(true);
    let summaryPayload = null;

    try {
      summaryPayload = await fetchRmsOutputs("summary", {
        business_unit_id: businessUnitId,
      });
      if (snapshotRequestRef.current !== requestId) return;
      setSummary(summaryPayload);

      const pinStatus = resolveRmsPipelineStatus(summaryPayload?.pipeline, {
        recordGroups: [
          {
            record: summaryPayload,
            requireRecord: true,
            requirePipelineRunId: true,
          },
          {
            record: summaryPayload?.daily_signals,
            requireRecord: true,
            requirePipelineRunId: true,
          },
          {
            record: summaryPayload?.recommendations,
            requireRecord: true,
            requirePipelineRunId: true,
          },
          {
            record: summaryPayload?.long_range_guidance,
            requireRecord: true,
            requirePipelineRunId: true,
          },
          {
            record: summaryPayload?.latest_runs?.cancellation_curves,
            requireRecord: true,
            requirePipelineRunId: true,
          },
          {
            record: summaryPayload?.latest_runs?.demand_forecast,
            requireRecord: true,
            requirePipelineRunId: true,
          },
          {
            record: summaryPayload?.latest_runs?.daily_features,
            requireRecord: true,
            requirePipelineRunId: true,
          },
          {
            record: summaryPayload?.latest_runs?.recommendations,
            requireRecord: true,
            requirePipelineRunId: true,
          },
          {
            record: summaryPayload?.latest_runs?.long_range_price_guidance,
            requireRecord: true,
            requirePipelineRunId: true,
          },
          {
            record: summaryPayload?.recommendations?.run,
            requireRecord: true,
            requirePipelineRunId: true,
          },
          {
            record: summaryPayload?.long_range_guidance?.run,
            requireRecord: true,
            requirePipelineRunId: true,
          },
        ],
      });
      const pipelineRunId = pinStatus.safeToRender ? pinStatus.pipeline?.id : null;
      if (!pipelineRunId) return;
      setPinnedPipelineId(pipelineRunId);

      const [recommendationsPayload, longRangePayload, workflowResult] = await Promise.all([
        fetchRmsOutputs("recommendations", {
          business_unit_id: businessUnitId,
          pipeline_run_id: pipelineRunId,
          room_category_id: roomCategoryId || undefined,
          date_from: dailyDateFrom,
          date_to: dailyDateTo,
          limit: DAILY_LIMIT,
        }),
        fetchRmsOutputs("long-range-guidance", {
          business_unit_id: businessUnitId,
          pipeline_run_id: pipelineRunId,
          room_category_id: roomCategoryId || undefined,
          date_from: longDateFrom,
          date_to: longDateTo,
          limit: LONG_RANGE_LIMIT,
        }),
        fetchRmsRecommendationWorkflow({
          businessUnitId,
          pipelineRunId,
        })
          .then((payload) => ({ payload, error: null }))
          .catch((workflowRequestError) => ({
            payload: null,
            error: workflowRequestError,
          })),
      ]);
      if (snapshotRequestRef.current !== requestId) return;

      setRecommendations(recommendationsPayload);
      setLongRange(longRangePayload);
      if (workflowResult.error) {
        const workflowFailure = getRmsWorkflowError(
          workflowResult.error,
          t("Could not load the recommendation workflow.")
        );
        setWorkflow(null);
        setWorkflowError(t(workflowFailure.message));
      } else {
        const normalizedWorkflow = normalizeRmsRecommendationWorkflow(
          workflowResult.payload,
          { businessUnitId, pipelineRunId }
        );
        setWorkflow(normalizedWorkflow);
        if (!normalizedWorkflow.valid) {
          setWorkflowError(
            t("The workflow contract could not be verified. Actions are disabled.")
          );
        }
      }
    } catch (nextError) {
      if (snapshotRequestRef.current !== requestId) return;
      if (!summaryPayload) setSummary(null);
      setRecommendations(null);
      setLongRange(null);
      setError(
        nextError?.response?.data?.error ||
          nextError?.message ||
          t("Could not load RMS recommendations.")
      );
    } finally {
      if (snapshotRequestRef.current === requestId) {
        setSummaryLoading(false);
        setDailyLoading(false);
        setLongRangeLoading(false);
        setWorkflowLoading(false);
      }
    }
  }, [
    businessUnitId,
    dailyDateFrom,
    dailyDateTo,
    longDateFrom,
    longDateTo,
    roomCategoryId,
    t,
  ]);

  useEffect(() => {
    void loadSnapshot();
    return () => {
      snapshotRequestRef.current += 1;
    };
  }, [loadSnapshot]);

  const isRefreshing =
    summaryLoading || dailyLoading || longRangeLoading || workflowLoading;

  const relatedPipelines = useMemo(
    () => [
      { label: t("Recommendations"), pipeline: recommendations?.pipeline },
      { label: t("Long range"), pipeline: longRange?.pipeline },
    ],
    [longRange, recommendations, t]
  );
  const snapshotRecordGroups = useMemo(
    () => [
      {
        label: t("Pinned snapshot"),
        record: pinnedPipelineId ? { pipeline_run_id: pinnedPipelineId } : null,
        requireRecord: true,
        requirePipelineRunId: true,
      },
      {
        label: t("Recommendations Overview"),
        record: summary,
        requireRecord: true,
        requirePipelineRunId: true,
      },
      {
        label: t("Recommendations"),
        record: recommendations,
        requireRecord: true,
        requirePipelineRunId: true,
      },
      {
        label: t("Long range"),
        record: longRange,
        requireRecord: true,
        requirePipelineRunId: true,
      },
      {
        label: t("Daily signals"),
        stage: "daily_features",
        record: summary?.daily_signals,
        requireRecord: true,
        requirePipelineRunId: true,
      },
      {
        label: t("Recommendations"),
        stage: "recommendations",
        record: summary?.recommendations,
        requireRecord: true,
        requirePipelineRunId: true,
      },
      {
        label: t("Long range"),
        stage: "long_range_price_guidance",
        record: summary?.long_range_guidance,
        requireRecord: true,
        requirePipelineRunId: true,
      },
      {
        label: t("Cancellation curves"),
        stage: "cancellation_curves",
        record: summary?.latest_runs?.cancellation_curves,
        requireRecord: true,
        requirePipelineRunId: true,
      },
      {
        label: t("Demand forecast"),
        stage: "demand_forecast",
        record: summary?.latest_runs?.demand_forecast,
        requireRecord: true,
        requirePipelineRunId: true,
      },
      {
        label: t("Daily features"),
        stage: "daily_features",
        record: summary?.latest_runs?.daily_features,
        requireRecord: true,
        requirePipelineRunId: true,
      },
      {
        label: t("Recommendations"),
        stage: "recommendations",
        record: summary?.latest_runs?.recommendations,
        requireRecord: true,
        requirePipelineRunId: true,
      },
      {
        label: t("Long range"),
        stage: "long_range_price_guidance",
        record: summary?.latest_runs?.long_range_price_guidance,
        requireRecord: true,
        requirePipelineRunId: true,
      },
      {
        label: t("Recommendation run"),
        stage: "recommendations",
        record: summary?.recommendations?.run,
        requireRecord: true,
        requirePipelineRunId: true,
      },
      {
        label: t("Long-range run"),
        stage: "long_range_price_guidance",
        record: summary?.long_range_guidance?.run,
        requireRecord: true,
        requirePipelineRunId: true,
      },
      {
        label: t("Recommendation run"),
        stage: "recommendations",
        record: recommendations?.run,
        requireRecord: true,
        requirePipelineRunId: true,
      },
      {
        label: t("Recommendation rows"),
        stage: "recommendations",
        records: recommendations?.rows,
        requireRecordsArray: true,
        requirePipelineRunId: true,
      },
      {
        label: t("Long-range run"),
        stage: "long_range_price_guidance",
        record: longRange?.run,
        requireRecord: true,
        requirePipelineRunId: true,
      },
      {
        label: t("Long-range rows"),
        stage: "long_range_price_guidance",
        records: longRange?.rows,
        requireRecordsArray: true,
        requirePipelineRunId: true,
      },
    ],
    [longRange, pinnedPipelineId, recommendations, summary, t]
  );
  const snapshotStatus = useMemo(
    () =>
      resolveRmsPipelineStatus(summary?.pipeline, {
        relatedPipelines,
        recordGroups: snapshotRecordGroups,
      }),
    [relatedPipelines, snapshotRecordGroups, summary]
  );
  const snapshotAligned = Boolean(
    pinnedPipelineId &&
      snapshotStatus.safeToRender &&
      snapshotStatus.pipeline?.id === pinnedPipelineId
  );
  const safeToRender = snapshotAligned && !isRefreshing;

  const refreshWorkflow = useCallback(
    async (pipelineRunId = pinnedPipelineId) => {
      if (!pipelineRunId) return null;
      try {
        setWorkflowLoading(true);
        setWorkflowError("");
        const payload = await fetchRmsRecommendationWorkflow({
          businessUnitId,
          pipelineRunId,
        });
        const normalized = normalizeRmsRecommendationWorkflow(payload, {
          businessUnitId,
          pipelineRunId,
        });
        setWorkflow(normalized);
        if (!normalized.valid) {
          setWorkflowError(
            t("The workflow contract could not be verified. Actions are disabled.")
          );
        }
        return normalized;
      } catch (nextError) {
        const failure = getRmsWorkflowError(
          nextError,
          t("Could not load the recommendation workflow.")
        );
        setWorkflow(null);
        setWorkflowError(t(failure.message));
        return null;
      } finally {
        setWorkflowLoading(false);
      }
    },
    [businessUnitId, pinnedPipelineId, t]
  );

  const workflowRowsByRecommendationId = useMemo(() => {
    const rowsById = new Map();
    safeArray(workflow?.rows).forEach((row) => {
      const recommendationId = Number(row?.recommendation_id);
      if (Number.isSafeInteger(recommendationId) && recommendationId > 0) {
        rowsById.set(recommendationId, row);
      }
    });
    return rowsById;
  }, [workflow]);

  const hasWorkflowPermission = useCallback(
    (action) =>
      action === "approve"
        ? canApproveRecommendations
        : action === "rollback"
          ? canRollbackRecommendations
          : canApplyRecommendations,
    [
      canApplyRecommendations,
      canApproveRecommendations,
      canRollbackRecommendations,
    ]
  );

  const handleWorkflowPreview = useCallback(
    async (action) => {
      if (
        workflowActionBusy ||
        !pinnedPipelineId ||
        !hasWorkflowPermission(action) ||
        !canRunRmsWorkflowAction(workflow, action)
      ) {
        return;
      }
      try {
        setWorkflowActionBusy(`preview:${action}`);
        setWorkflowError("");
        setWorkflowNotice("");
        setWorkflowNote("");
        const payload = await previewRmsRecommendationWorkflowAction(
          action,
          buildRmsWorkflowPreviewPayload({
            businessUnitId,
            pipelineRunId: pinnedPipelineId,
          })
        );
        const preview = normalizeRmsWorkflowPreview(payload, action);
        if (!preview.valid) {
          setWorkflowError(
            t("The workflow preview could not be verified. Nothing was changed.")
          );
          return;
        }
        setWorkflowDialog({
          action,
          preview,
          idempotencyKey: createRmsWorkflowIdempotencyKey(),
        });
      } catch (nextError) {
        const failure = getRmsWorkflowError(
          nextError,
          t("Could not preview this workflow action.")
        );
        setWorkflowError(t(failure.message));
        if (failure.status === 409) {
          await refreshWorkflow(pinnedPipelineId);
        }
      } finally {
        setWorkflowActionBusy("");
      }
    },
    [
      businessUnitId,
      hasWorkflowPermission,
      pinnedPipelineId,
      refreshWorkflow,
      t,
      workflow,
      workflowActionBusy,
    ]
  );

  const handleWorkflowExecute = useCallback(async () => {
    const action = workflowDialog?.action;
    const preview = workflowDialog?.preview;
    if (
      !action ||
      workflowActionBusy ||
      !hasWorkflowPermission(action) ||
      !canExecuteRmsWorkflowPreview(preview)
    ) {
      if (preview?.valid && !canExecuteRmsWorkflowPreview(preview)) {
        setWorkflowError(t("The preview expired. Generate a new preview."));
      }
      return;
    }

    try {
      setWorkflowActionBusy(`execute:${action}`);
      setWorkflowError("");
      const result = await executeRmsRecommendationWorkflowAction(
        action,
        buildRmsWorkflowExecutePayload({
          businessUnitId,
          previewToken: preview.previewToken,
          idempotencyKey: workflowDialog.idempotencyKey,
          note: workflowNote,
        })
      );
      const executionResult = normalizeRmsWorkflowExecutionResult(result, {
        action,
        businessUnitId,
        pipelineRunId: pinnedPipelineId,
        idempotencyKey: workflowDialog.idempotencyKey,
      });
      if (!executionResult.valid) {
        setWorkflowError(
          t("The workflow execution response could not be verified. Refresh its status.")
        );
        setWorkflowDialog(null);
        setWorkflowNote("");
        await refreshWorkflow(pinnedPipelineId);
        return;
      }
      setWorkflow(executionResult.workflow);
      setWorkflowNotice(
        action === "approve"
          ? t("The complete recommendation run was approved.")
          : action === "apply"
            ? t(
                "The complete run was applied in the PMS."
              )
            : t(
                "The complete application was rolled back in the PMS."
              )
      );
      setWorkflowDialog(null);
      setWorkflowNote("");
    } catch (nextError) {
      const failure = getRmsWorkflowError(
        nextError,
        t("Could not execute this workflow action.")
      );
      setWorkflowError(t(failure.message));
      if (failure.status === 409) {
        setWorkflowDialog(null);
        setWorkflowNote("");
        await refreshWorkflow(pinnedPipelineId);
      }
      // A network timeout intentionally keeps the same dialog and idempotency key.
    } finally {
      setWorkflowActionBusy("");
    }
  }, [
    businessUnitId,
    hasWorkflowPermission,
    pinnedPipelineId,
    refreshWorkflow,
    t,
    workflowActionBusy,
    workflowDialog,
    workflowNote,
  ]);

  const summaryCards = useMemo(() => {
    const recommendationPriceSummary = resolveCurrencyPriceSummary(
      summary?.recommendations
    );
    const longRangePriceSummary = resolveCurrencyPriceSummary(
      summary?.long_range_guidance
    );
    const priceSummaryMeta = (priceSummary, defaultText) => {
      if (priceSummary.status === "multiple") {
        return t("Separate averages by currency; amounts are not combined.");
      }
      if (priceSummary.status === "unknown") {
        return t("Average unavailable because currency could not be verified.");
      }
      return defaultText;
    };

    return [
      {
        label: t("Daily recommendation rows"),
        value: summary?.recommendations?.rows_count ?? "-",
        meta: `${recommendations?.returned_rows || 0}/${recommendations?.total_rows || 0} ${t("Rows loaded").toLowerCase()}`,
      },
      {
        label: t("Avg recommended daily price"),
        value: (
          <CurrencyAverageValue
            priceSummary={recommendationPriceSummary}
            locale={locale}
          />
        ),
        meta: priceSummaryMeta(
          recommendationPriceSummary,
          t("Latest successful recommendations run")
        ),
      },
      {
        label: t("Avg daily confidence"),
        value:
          summary?.recommendations?.avg_confidence != null
            ? String(summary.recommendations.avg_confidence)
            : "-",
        meta: t("Effective forecast and feature evidence used by the movement policy"),
      },
      {
        label: t("Rows pending review"),
        value: summary?.recommendations?.pending_apply_count ?? "-",
        meta: t("Recommendations not yet reviewed or used operationally"),
      },
      {
        label: t("Long-range rows"),
        value: summary?.long_range_guidance?.rows_count ?? "-",
        meta: t("{count} blocks generated", {
          count: summary?.long_range_guidance?.blocks_count ?? 0,
        }),
      },
      {
        label: t("Avg long-range price"),
        value: (
          <CurrencyAverageValue priceSummary={longRangePriceSummary} locale={locale} />
        ),
        meta: priceSummaryMeta(
          longRangePriceSummary,
          t("Average block guidance beyond detailed horizon")
        ),
      },
      {
        label: t("Market signal coverage"),
        value: formatPercent(summary?.daily_signals?.market_signal_coverage_pct, 1, locale),
        meta: t("{count} daily rows with comparable benchmark", {
          count: summary?.daily_signals?.rows_with_market_signal ?? 0,
        }),
      },
      {
        label: t("Stop-sell rows"),
        value: summary?.daily_signals?.stop_sell_rows ?? "-",
        meta: t("Rows already protected by stop-sell"),
      },
    ];
  }, [locale, recommendations, summary, t]);

  return (
    <div className="rmo-sectionStack">
      <div className="rmo-banner">
        <div className="rmo-bannerText">
          {t(
            "Here you can review the final daily recommendations and the longer-term guidance proposed by the optimizer before taking any pricing decision."
          )}
        </div>
        <div className="rmo-inlineActions">
          <RunPill
            label={t("Daily features")}
            run={summary?.latest_runs?.daily_features}
            languageId={languageId}
          />
          <RunPill
            label={t("Recommendations")}
            run={summary?.recommendations?.run}
            languageId={languageId}
          />
          <RunPill
            label={t("Long range")}
            run={summary?.long_range_guidance?.run}
            languageId={languageId}
          />
        </div>
      </div>

      <RmsPipelineStatus
        pipeline={summary?.pipeline}
        t={t}
        locale={locale}
        loading={isRefreshing}
        relatedPipelines={relatedPipelines}
        recordGroups={snapshotRecordGroups}
      />

      {error ? <div className="rmo-errorBox">{error}</div> : null}

      <div className="rmo-surface">
        <div className="rmo-head">
          <div>
            <h3 className="rmo-headTitle">{t("Recommendations Overview")}</h3>
            <div className="rmo-headSubtitle">
              {t("Final optimizer output for near-term dates and longer-term guidance.")}
            </div>
          </div>
        </div>

        <div className="rmo-toolbar">
          <div className="rmo-toolbarGroup">
            <select
              className="rmo-select"
              value={roomCategoryId}
              onChange={(event) => setRoomCategoryId(event.target.value)}
            >
              <option value="">{t("All room categories")}</option>
              {safeArray(meta?.room_categories).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>

            <span className="rmo-pill rmo-pill--soft">{t("Daily window")}</span>
            <input
              className="rmo-input"
              type="date"
              value={dailyDateFrom}
              onChange={(event) => setDailyDateFrom(event.target.value)}
            />
            <input
              className="rmo-input"
              type="date"
              value={dailyDateTo}
              onChange={(event) => setDailyDateTo(event.target.value)}
            />

            <span className="rmo-pill rmo-pill--soft">{t("Long-range window")}</span>
            <input
              className="rmo-input"
              type="date"
              value={longDateFrom}
              onChange={(event) => setLongDateFrom(event.target.value)}
            />
            <input
              className="rmo-input"
              type="date"
              value={longDateTo}
              onChange={(event) => setLongDateTo(event.target.value)}
            />
          </div>

          <div className="rmo-toolbarGroup">
            <button
              type="button"
              className="rmo-btn rmo-btn--accent"
              onClick={() => {
                void loadSnapshot();
              }}
              disabled={isRefreshing}
            >
              {isRefreshing ? t("Refreshing...") : t("Refresh")}
            </button>
          </div>
        </div>

        {!isRefreshing && !snapshotAligned ? (
          <div className="rmo-empty">
            {t("Snapshot data is hidden because pipeline identity could not be verified.")}
          </div>
        ) : null}

        {safeToRender ? (
          <div className="rmo-kpiGrid">
            {summaryCards.map((card) => (
              <div key={card.label} className="rmo-kpiCard">
                <div className="rmo-kpiLabel">{card.label}</div>
                <div className="rmo-kpiValue">{card.value}</div>
                <div className="rmo-kpiMeta">{card.meta}</div>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <div className="rmo-surface rmo-workflowSurface">
        <div className="rmo-head">
          <div>
            <h3 className="rmo-headTitle">{t("Approval & application workflow")}</h3>
            <div className="rmo-headSubtitle">
              {t(
                "Approve, apply or roll back the complete certified run. Recommendation decisions remain immutable."
              )}
            </div>
          </div>
          {workflow?.valid ? (
            <span
              className={`rmo-pill rmo-pill--${getRmsWorkflowStatusTone(
                workflow.status
              )}`}
            >
              {translateRmsStatus(languageId, workflow.status)}
            </span>
          ) : null}
        </div>

        {workflowError ? <div className="rmo-errorBox">{workflowError}</div> : null}
        {workflowNotice ? (
          <div className="rmo-workflowNotice">{workflowNotice}</div>
        ) : null}

        {workflowLoading ? (
          <div className="rmo-empty">{t("Loading recommendation workflow...")}</div>
        ) : !workflow?.valid ? (
          <div className="rmo-empty">
            {t("Operational actions are disabled because the workflow could not be verified.")}
          </div>
        ) : (
          <>
            <div className="rmo-workflowBody">
              <WorkflowSummaryGrid summary={workflow.summary} locale={locale} t={t} />
              <WorkflowBlockers blockers={workflow.blockers} t={t} />
              {workflow.latestOperation ? (
                <div className="rmo-workflowLatest">
                  <strong>{t("Latest operation")}</strong>
                  <span>
                    {t(humanizeWorkflowField(workflow.latestOperation.action || "operation"))}
                    {workflow.latestOperation.status
                      ? ` · ${translateRmsStatus(
                          languageId,
                          workflow.latestOperation.status
                        )}`
                      : ""}
                  </span>
                  {workflow.latestOperation.completed_at ? (
                    <small>
                      {new Date(workflow.latestOperation.completed_at).toLocaleString(locale)}
                    </small>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="rmo-workflowActionBar">
              <div>
                <strong>{t("Full-run operations only")}</strong>
                <span>
                  {t(
                    "Held-by-policy rows remain certified no-ops and are never forced into an application."
                  )}
                </span>
              </div>
              <div className="rmo-inlineActions">
                <button
                  type="button"
                  className="rmo-btn"
                  disabled={
                    Boolean(workflowActionBusy) ||
                    !canApproveRecommendations ||
                    !canRunRmsWorkflowAction(workflow, "approve")
                  }
                  onClick={() => void handleWorkflowPreview("approve")}
                  title={
                    canApproveRecommendations
                      ? undefined
                      : t("You do not have permission to approve RMS runs.")
                  }
                >
                  {workflowActionBusy === "preview:approve"
                    ? t("Preparing preview...")
                    : t("Approve run")}
                </button>
                <button
                  type="button"
                  className="rmo-btn rmo-btn--accent"
                  disabled={
                    Boolean(workflowActionBusy) ||
                    !canApplyRecommendations ||
                    !canRunRmsWorkflowAction(workflow, "apply")
                  }
                  onClick={() => void handleWorkflowPreview("apply")}
                  title={
                    canApplyRecommendations
                      ? undefined
                      : t("You do not have permission to apply RMS runs.")
                  }
                >
                  {workflowActionBusy === "preview:apply"
                    ? t("Preparing preview...")
                    : t("Apply approved run")}
                </button>
                <button
                  type="button"
                  className="rmo-btn rmo-btn--danger"
                  disabled={
                    Boolean(workflowActionBusy) ||
                    !canRollbackRecommendations ||
                    !canRunRmsWorkflowAction(workflow, "rollback")
                  }
                  onClick={() => void handleWorkflowPreview("rollback")}
                  title={
                    canRollbackRecommendations
                      ? undefined
                      : t("You do not have permission to roll back RMS applications.")
                  }
                >
                  {workflowActionBusy === "preview:rollback"
                    ? t("Preparing preview...")
                    : t("Rollback application")}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="rmo-surface">
        <div className="rmo-head">
          <div>
            <h3 className="rmo-headTitle">{t("Daily Recommendations")}</h3>
            <div className="rmo-headSubtitle">
              {t("Detailed horizon from {from} to {to}.", {
                from: dailyDateFrom || "-",
                to: dailyDateTo || "-",
              })}
            </div>
          </div>
        </div>

        <div className="rmo-aiOriginGuide">
          <strong>{t("How to read Origin & AI")}</strong>
          <span>
            {t(
              "AI-assisted explanation never changes price or stay rules. Only Validated AI number means an AI numeric proposal passed deterministic validation."
            )}
          </span>
        </div>

        {dailyLoading ? (
          <div className="rmo-empty">{t("Loading daily recommendations...")}</div>
        ) : !safeToRender ? (
          <div className="rmo-empty">
            {t("Snapshot data is hidden because pipeline identity could not be verified.")}
          </div>
        ) : !safeArray(recommendations?.rows).length ? (
          <div className="rmo-empty">{t("No daily recommendations found for the selected filter.")}</div>
        ) : (
          <div
            className="rmo-tableWrap"
            style={{ "--rmo-col-1": "190px", "--rmo-col-2": "128px" }}
          >
            <table className="rmo-table">
              <thead>
                <tr>
                  <th className="rmo-sticky s1">{t("Room category")}</th>
                  <th className="rmo-sticky s2">{t("Stay date")}</th>
                  <th>{t("Product")}</th>
                  <th>{t("Current price")}</th>
                  <th>{t("Base price")}</th>
                  <th>{t("Market baseline")}</th>
                  <th>{t("Current occ")}</th>
                  <th>{t("Final occ")}</th>
                  <th>{t("Stop sell")}</th>
                  <th>{t("Recommended price")}</th>
                  <th>{t("Price controls")}</th>
                  <th>{t("Origin & AI")}</th>
                  <th>{t("Delta vs current")}</th>
                  <th>{t("Min stay")}</th>
                  <th>{t("Max stay")}</th>
                  <th>{t("Confidence")}</th>
                  <th>{t("Confidence controls")}</th>
                  <th>{t("Gap control")}</th>
                  <th>{t("Recommendation decision")}</th>
                  <th>{t("Operational workflow")}</th>
                  <th>{t("Reason")}</th>
                </tr>
              </thead>
              <tbody>
                {recommendations.rows.map((row) => {
                  const signal = row.signal_snapshot || {};
                  const currency = row.room_rate_currency;
                  const stopSell = normalizeSnapshotBoolean(signal.is_stop_sell);
                  const workflowRow = workflowRowsByRecommendationId.get(Number(row.id));
                  const workflowRowStatus = resolveRmsWorkflowRowStatus(workflowRow);
                  return (
                    <tr key={`${row.room_category_id}-${row.stay_date}`}>
                      <td className="rmo-sticky s1">{row.room_category_name || "-"}</td>
                      <td className="rmo-sticky s2">
                        <div>{formatDateLabel(row.stay_date, dateLocale)}</div>
                        <div className="rmo-muted">{row.stay_date}</div>
                      </td>
                      <td>
                        <RateProductTrace row={row} t={t} />
                      </td>
                      <td>
                        <SnapshotCurrencyValue
                          value={signal.current_price}
                          locale={locale}
                          currency={currency}
                        />
                      </td>
                      <td>
                        <SnapshotCurrencyValue
                          value={signal.expected_base_price}
                          locale={locale}
                          currency={currency}
                        />
                      </td>
                      <td>
                        <div className="rmo-cellStack">
                          <div className="rmo-cellMain">
                            <SnapshotCurrencyValue
                              value={signal.comp_price_weighted_avg_equiv}
                              locale={locale}
                              currency={currency}
                            />
                          </div>
                          <div className="rmo-cellMeta">
                            {t("Sources: {count}", { count: signal.comp_sources_count_equiv ?? "-" })}
                          </div>
                        </div>
                      </td>
                      <td>{formatPercent(signal.current_occupancy_pct, 1, locale)}</td>
                      <td>{formatPercent(signal.expected_final_occupancy_pct, 1, locale)}</td>
                      <td>
                        <span
                          className={`rmo-pill rmo-pill--${
                            stopSell === true ? "danger" : "soft"
                          }`}
                        >
                          {stopSell == null ? "—" : stopSell ? t("Yes") : t("No")}
                        </span>
                      </td>
                      <td>
                        <SnapshotCurrencyValue
                          value={row.recommended_price}
                          locale={locale}
                          currency={currency}
                        />
                      </td>
                      <td>
                        <PriceResponseCell
                          row={row}
                          locale={locale}
                          currency={currency}
                          t={t}
                        />
                      </td>
                      <td style={{ minWidth: 285 }}>
                        <RecommendationOriginCell row={row} t={t} />
                      </td>
                      <td>
                        <RecommendationDelta
                          recommendedPrice={row.recommended_price}
                          currentPrice={signal.current_price}
                          locale={locale}
                          currency={currency}
                        />
                      </td>
                      <td>{row.recommended_min_stay ?? signal.min_stay ?? "-"}</td>
                      <td>{row.recommended_max_stay ?? signal.max_stay ?? "-"}</td>
                      <td>{row.confidence ?? "-"}</td>
                      <td><ConfidenceControlCell row={row} locale={locale} t={t} /></td>
                      <td>
                        <OrphanGapActionCell
                          row={row}
                          locale={locale}
                          currency={currency}
                          t={t}
                        />
                      </td>
                      <td>
                        <span
                          className={`rmo-pill rmo-pill--${statusTone(
                            row.apply_status || "pending"
                          )}`}
                        >
                          {translateRmsStatus(languageId, row.apply_status || "pending")}
                        </span>
                      </td>
                      <td>
                        {workflowRowStatus ? (
                          <span
                            className={`rmo-pill rmo-pill--${getRmsWorkflowStatusTone(
                              workflowRowStatus
                            )}`}
                          >
                            {translateRmsStatus(languageId, workflowRowStatus)}
                          </span>
                        ) : (
                          <span className="rmo-muted">—</span>
                        )}
                      </td>
                      <td style={{ minWidth: 320 }}>{row.reason_short || "-"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="rmo-surface">
        <div className="rmo-head">
          <div>
            <h3 className="rmo-headTitle">{t("Long-range Guidance")}</h3>
            <div className="rmo-headSubtitle">
              {t("Guidance blocks from {from} to {to}.", {
                from: longDateFrom || "-",
                to: longDateTo || "-",
              })}
            </div>
          </div>
        </div>

        {longRangeLoading ? (
          <div className="rmo-empty">{t("Loading long-range guidance...")}</div>
        ) : !safeToRender ? (
          <div className="rmo-empty">
            {t("Snapshot data is hidden because pipeline identity could not be verified.")}
          </div>
        ) : !safeArray(longRange?.rows).length ? (
          <div className="rmo-empty">{t("No long-range guidance rows found for the selected filter.")}</div>
        ) : (
          <div
            className="rmo-tableWrap"
            style={{ "--rmo-col-1": "190px", "--rmo-col-2": "120px" }}
          >
            <table className="rmo-table">
              <thead>
                <tr>
                  <th className="rmo-sticky s1">{t("Room category")}</th>
                  <th className="rmo-sticky s2">{t("Block type")}</th>
                  <th>{t("Product")}</th>
                  <th>{t("Date range")}</th>
                  <th>{t("Seasonality template")}</th>
                  <th>{t("Recommended price")}</th>
                  <th>{t("Min stay")}</th>
                  <th>{t("Confidence")}</th>
                  <th>{t("Reason")}</th>
                </tr>
              </thead>
              <tbody>
                {longRange.rows.map((row) => (
                  <tr key={row.id}>
                    <td className="rmo-sticky s1">{row.room_category_name || "-"}</td>
                    <td className="rmo-sticky s2">
                      <div className="rmo-cellStack">
                        <div className="rmo-cellMain">{row.block_type || "-"}</div>
                        <div className="rmo-cellMeta">{t("Block #{id}", { id: row.block_id })}</div>
                      </div>
                    </td>
                    <td>
                      <RateProductTrace row={row} t={t} />
                    </td>
                    <td>
                      <div className="rmo-cellStack">
                        <div className="rmo-cellMain">
                          {formatDateRange(row.date_from, row.date_to, locale)}
                        </div>
                        <div className="rmo-cellMeta">
                          {formatDateLabel(row.date_from, dateLocale)} -> {formatDateLabel(row.date_to, dateLocale)}
                        </div>
                      </div>
                    </td>
                    <td>{row.seasonality_template_name || "-"}</td>
                    <td>
                      <SnapshotCurrencyValue
                        value={row.recommended_price}
                        locale={locale}
                        currency={row.room_rate_currency}
                      />
                    </td>
                    <td>{row.recommended_min_stay ?? "-"}</td>
                    <td>{row.confidence_score ?? "-"}</td>
                    <td style={{ minWidth: 320 }}>{row.reason_short || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PmsConfirmDialog
        isOpen={Boolean(workflowDialog)}
        title={t(getRmsWorkflowActionCopy(workflowDialog?.action).title)}
        message={
          workflowDialog ? (
            <WorkflowPreviewBody
              preview={workflowDialog.preview}
              note={workflowNote}
              onNoteChange={setWorkflowNote}
              locale={locale}
              t={t}
            />
          ) : null
        }
        confirmLabel={t(getRmsWorkflowActionCopy(workflowDialog?.action).confirm)}
        cancelLabel={t("Cancel")}
        onConfirm={() => void handleWorkflowExecute()}
        onClose={() => {
          if (!workflowActionBusy) {
            setWorkflowDialog(null);
            setWorkflowNote("");
          }
        }}
        isBusy={workflowActionBusy.startsWith("execute:")}
        confirmDisabled={
          !workflowDialog ||
          !hasWorkflowPermission(workflowDialog.action) ||
          !canExecuteRmsWorkflowPreview(
            workflowDialog.preview,
            workflowPreviewClock
          )
        }
        tone={workflowDialog?.action === "rollback" ? "warning" : "primary"}
        closeOnOverlayClick={false}
        closeOnEscape={!workflowActionBusy}
        eyebrow={t("Revenue Optimizer")}
      />
    </div>
  );
}
